import { useState, useEffect, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Monitor } from "lucide-react";
import {
  getThemeMode,
  setThemeMode,
  applyTheme,
  getResolvedTheme,
  type ThemeMode,
} from "../utils/theme";

interface ThemeToggleProps {
  className?: string;
  variant?: "segmented" | "cycle";
  showLabel?: boolean;
}

const themeOptions: Array<{ mode: ThemeMode; label: string; icon: typeof Sun }> = [
  { mode: "light", label: "Light", icon: Sun },
  { mode: "system", label: "System", icon: Monitor },
  { mode: "dark", label: "Dark", icon: Moon },
];

export function ThemeToggle({
  className = "",
  variant = "segmented",
  showLabel = false,
}: ThemeToggleProps) {
  const instanceId = useId();
  const [currentMode, setCurrentMode] = useState<ThemeMode>("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const mode = getThemeMode();
    setCurrentMode(mode);
    applyTheme(mode);

    const handleThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ mode: ThemeMode }>;
      if (customEvent.detail?.mode) {
        setCurrentMode(customEvent.detail.mode);
      } else {
        setCurrentMode(getThemeMode());
      }
    };

    window.addEventListener("radiece_theme_change", handleThemeChange);

    // Live media query listener for system mode
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemMediaChange = () => {
      if (getThemeMode() === "system") {
        applyTheme("system");
        setCurrentMode("system");
      }
    };

    mediaQuery.addEventListener("change", handleSystemMediaChange);

    return () => {
      window.removeEventListener("radiece_theme_change", handleThemeChange);
      mediaQuery.removeEventListener("change", handleSystemMediaChange);
    };
  }, []);

  const handleSelectMode = (mode: ThemeMode) => {
    setCurrentMode(mode);
    setThemeMode(mode);
  };

  const handleCycle = () => {
    let next: ThemeMode = "light";
    if (currentMode === "light") next = "dark";
    else if (currentMode === "dark") next = "system";
    else next = "light";

    handleSelectMode(next);
  };

  if (!mounted) {
    return (
      <div
        className={`inline-flex items-center p-1 rounded-full bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 ${className}`}
        aria-hidden="true"
      >
        <div className="w-6 h-6 rounded-full bg-transparent" />
      </div>
    );
  }

  // Variant 1: Cycle Button (1-Click Cycle for compact mobile bars)
  if (variant === "cycle") {
    const resolved = getResolvedTheme(currentMode);
    const isDark = resolved === "dark";

    return (
      <button
        onClick={handleCycle}
        type="button"
        role="button"
        aria-label={`Theme mode: ${currentMode} (click to toggle)`}
        title={`Active: ${currentMode.toUpperCase()} mode (Click to cycle Light → Dark → System)`}
        className={`relative inline-flex items-center justify-center p-2 rounded-full border transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary select-none ${
          isDark
            ? "bg-slate-800/90 border-slate-700 text-amber-400 hover:bg-slate-700 hover:border-amber-400/40 shadow-xs"
            : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200 hover:text-brand hover:border-slate-300 shadow-xs"
        } ${className}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          {currentMode === "light" && (
            <motion.div
              key="sun"
              initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex items-center justify-center"
            >
              <Sun className="w-4 h-4 text-amber-500" />
            </motion.div>
          )}

          {currentMode === "dark" && (
            <motion.div
              key="moon"
              initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="flex items-center justify-center"
            >
              <Moon className="w-4 h-4 text-sky-400" />
            </motion.div>
          )}

          {currentMode === "system" && (
            <motion.div
              key="system"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="relative flex items-center justify-center"
            >
              <Monitor className="w-4 h-4 text-primary" />
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>

        {showLabel && (
          <span className="ml-2 text-xs font-semibold capitalize">
            {currentMode}
          </span>
        )}
      </button>
    );
  }

  // Variant 2: Segmented Pill Switcher (Light | System | Dark)
  return (
    <div
      role="radiogroup"
      aria-label="Theme mode"
      className={`relative inline-flex items-center p-0.5 rounded-full bg-slate-100 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700/80 shadow-xs transition-colors ${className}`}
    >
      {themeOptions.map((opt) => {
        const Icon = opt.icon;
        const isSelected = currentMode === opt.mode;

        return (
          <button
            key={opt.mode}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => handleSelectMode(opt.mode)}
            title={`${opt.label} Mode`}
            className={`relative flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              isSelected
                ? "text-brand-dark dark:text-slate-100 font-bold"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            }`}
          >
            {isSelected && (
              <motion.span
                layoutId={`themeSegmentPill-${instanceId}`}
                className="absolute inset-0 bg-white dark:bg-slate-700 rounded-full shadow-xs border border-slate-200/80 dark:border-slate-600/80"
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            )}

            <span className="relative z-10 flex items-center gap-1.5">
              <Icon
                className={`w-3.5 h-3.5 shrink-0 transition-colors ${
                  opt.mode === "light" && isSelected
                    ? "text-amber-500"
                    : opt.mode === "dark" && isSelected
                    ? "text-sky-400"
                    : opt.mode === "system" && isSelected
                    ? "text-primary"
                    : ""
                }`}
              />

              {showLabel && <span>{opt.label}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
